<template>
  <div ref="container" class="p-l-10 p-r-10 p-t-5 p-b-5">
    <template v-if="hasTranscription">
      <div
        v-if="isLoadingTranscription"
        class="ag-custom-loading-cell p-l-15 p-t-5"
        style="line-height: 25px;"
      >
        <i class="fa fa-spinner fa-pulse fa-fw" /> 
        <span v-t="'TRANSCRIPTION_LOADING'" />
      </div>
      <div
        v-if="transcriptionError"
        class="bs-callout bs-callout-sm bs-callout-danger"
      >
        <p>{{ $t('TRANSCRIPTION_ERROR') }}</p>
      </div>
      <table
        v-if="transcriptionSuccess"
        class="table_basic call-log-sub-table-record condensed"
      >
        <tbody>
          <tr>
            <th v-t="'TRANSCRIPT_COST'" scope="row" />
            <td>{{ callDetails.transcriptionCost | currency }}</td>
          </tr>
          <tr>
            <th v-t="'TRANSCRIPTION'" scope="row" />
            <td>
              <div
                v-clipboard="{ copyText: transcriptionCopyText }"
                class="clickable display-flex flex--align-center"
              >
                {{ $t('COPY_TRANSCRIPT') }}
                <button class="btn btn-link m-l-5 p-0 no_border"
                >
                  <i class="typcn typcn-tabs-outline" />
                </button>
              </div>
            </td>
          </tr>
          <tr class="transcript-row">
            <td colspan="2">
              <div
                :class="{ 'transcript--empty': isEmptyTranscript }"
                class="transcript"
              >
                <div class="transcript__content">
                  <div v-if="isEmptyTranscript">
                    {{ $t('TRANSCRIPTION_EMPTY') }}
                  </div>
                  <template v-else>
                    <div
                      v-for="(utterance, index) in transcription.utterances"
                      :key="index"
                      class="transcript__utterance"
                    >
                      <span class="transcript__time">
                        {{ formatSeconds(utterance.start) }} - 
                      </span>
                      <span
                        v-if="utterance.channel === 0"
                        class="transcript__channel transcript__channel--alt"
                      >
                        {{ $t('TRANSCRIPTION_CALLER') }}:
                      </span>
                      <span
                        v-else
                        class="transcript__channel"
                      >
                        {{ $t('TRANSCRIPTION_TARGET') }}:
                      </span>
                      <span>{{ utterance.transcript }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <div
      v-else
      class="p-15"
    >
      <template v-if="isTranscriptionPending || sentTranscriptionRequest">
        <p class="m-b-15">{{ $t('TRANSCRIPTION_PENDING') }}</p>
      </template>
      <template v-else>
        <p class="m-b-15">{{ $t('TRANSCRIPTION_AVAILABLE') }}</p>
        <button
          class="btn btn-primary btn-loader p-l-15"
          :class="isTranscribing ? 'p-r-35' : 'p-r-15'"
          :disabled="isTranscribing"
          @click="transcribeCall"
        >
          <i class="fa fa-file-text-o" />
          {{ $t('TRANSCRIBE') }} 
          <Loader v-if="isTranscribing" custom-class="size-sm" />
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { currencyFormatter } from '@/modules/reporting/components/AgGridUtils';
import messages from '../../../locales';
import sharedMessages from '@/core/locales';
import callLogDetailMixin from '../mixins/callLogDetailMixin';
import { STATUS_ERROR } from '@/store/helpers';
import TruncatedTextCopy from '@/angularBridgeCode/components/TruncatedTextCopy';
import Loader from '@/core/components/Loader';

export default {
  name: 'TranscriptionCallLogDetail',
  filters: {
    currency(value) {
      return currencyFormatter.format(value);
    }
  },
  components: {
    TruncatedTextCopy,
    Loader
  },
  mixins: [callLogDetailMixin],

  props: {
    timeZoneId: {
      type: String,
      default() {
        return moment.tz.guess();
      }
    },
    callDetails: {
      type: Object,
      default: () => ({}),
    },
    role: {
      type: String,
      default: ''
    },
    hasTranscription: {
      type: Boolean,
      default() {
        return false;
      }
    },
    isTranscriptionPending: {
      type: Boolean,
      default() {
        return false;
      }
    },
  },

  data() {
    return {
      isTranscribing: false,
      sentTranscriptionRequest: false
    };
  },
  computed: {
    transcription() {
      return this.callDetails?.transcriptionDetails?.transcription;
    },
    isLoadingTranscription() {
      return !this.callDetails?.transcriptionDetails;
    },
    transcriptionSuccess() {
      return this.callDetails?.transcriptionDetails?.transcription?.success;
    },
    transcriptionError() {
      return this.callDetails?.transcriptionDetails?.transcriptionStatus === STATUS_ERROR || this.callDetails?.transcriptionDetails?.transcription?.success === false || this.callDetails?.transcriptionDetails?.transcription === null;
    },
    transcriptionCopyText() {
      let transcriptionText = '';
      if (this.isEmptyTranscript) {
        transcriptionText = this.$i18n.t('TRANSCRIPTION_EMPTY');
      } else {
        this.transcription?.utterances?.forEach((utterance) => {
          const timestamp = this.formatSeconds(utterance.start);
          const channel = utterance.channel === 0 ? 'Caller:' : 'Target:';
          transcriptionText += timestamp + ' - ' + channel + '\u00A0' + utterance.transcript + '\n\n';
        });
      }
      return transcriptionText;
    },
    isEmptyTranscript() {
      return !this.transcription?.utterances?.length;
    }
  },

  methods: {
    ...mapActions('reporting', ['transcribeMultipleCalls']),
    async transcribeCall() {
      this.isTranscribing = true;
      const payload = {
        callIds: [this.callDetails.inboundCallId],
        resetDetails: false
      }
      try {
        await this.transcribeMultipleCalls(payload);
        utilities.showToaster('success', this.$i18n.t('TRANSCRIBE_SUCCESS'));
        this.sentTranscriptionRequest = true;
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        this.isTranscribing = false;
      }
    },
    formatSeconds(seconds) {
      const duration = moment.duration(seconds, 'seconds');
      return moment.utc(duration.asMilliseconds()).format('HH:mm:ss');
    },
  },
  i18n: {
    locale: 'en',
    messages,
    sharedMessages
  }
};
</script>